import { faCircleCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { Trans } from '@lingui/react/macro';
import { Badge } from '@repo/videa-components';

import { StatusMessage } from '@/common-components/elements/StatusMessage/StatusMessage';

import type { PreQualifyFormValues } from '../types';
import { shouldShowDownload } from '../utils';

import { DownloadSection } from './DownloadSection';

interface PreQualifySuccessProps {
  dealId: string;
  values: PreQualifyFormValues;
  onStartOver: () => void;
}

export const PreQualifySuccess = ({
  dealId,
  values,
  onStartOver,
}: PreQualifySuccessProps) => {
  const showDownload = shouldShowDownload(values);
  const { firstName } = values;

  return (
    <div className="vai-space-y-6">
      <StatusMessage
        icon={faCircleCheck}
        iconClassName="vai-h-6 vai-w-6 vai-text-green-600"
        circleClassName="vai-bg-green-100"
        heading={<Trans>Thank you, {firstName}!</Trans>}
        headingClassName="vai-text-xl vai-font-semibold"
        message={
          showDownload ? (
            <Trans>Your pre-qualifier is ready to download.</Trans>
          ) : (
            <Trans>
              Your information has been recorded. Our team will reach out to you
              shortly.
            </Trans>
          )
        }
        messageClassName="vai-text-center vai-text-sm vai-text-gray-500"
        className="vai-flex vai-flex-col vai-items-center vai-space-y-2"
      />

      <div className="vai-rounded-lg vai-border vai-border-green-200 vai-bg-green-50 vai-p-4">
        <h3 className="vai-mb-3 vai-text-base vai-font-semibold vai-text-green-900">
          Your Information
        </h3>
        <dl className="vai-space-y-2 vai-text-sm">
          <InfoRow
            label="Name"
            value={`${values.firstName} ${values.lastName}`}
          />
          <InfoRow label="Business" value={values.companyName} />
          <InfoRow label="Email" value={values.email} />
          <InfoRow label="Phone" value={values.phone} />
          <InfoRow
            label="IT Contact Email"
            value={values.itContactEmail}
          />
          {values.productRequest && (
            <div className="vai-flex vai-items-center vai-justify-between">
              <dt className="vai-text-gray-600">Product Request:</dt>
              <dd>
                <Badge variant="secondary">{values.productRequest}</Badge>
              </dd>
            </div>
          )}
          <InfoRow label="PMS System" value={values.primaryPms} />
          <InfoRow label="Imaging System" value={values.imagingPlatform} />
        </dl>
      </div>

      <DownloadSection show={showDownload} dealId={dealId} />

      <button
        onClick={onStartOver}
        className="vai-mx-auto vai-flex vai-items-center vai-gap-1 vai-text-sm vai-text-gray-500 hover:vai-text-gray-700"
      >
        <svg
          className="vai-h-4 vai-w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
          />
        </svg>
        Start Over
      </button>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => {
  if (!value) return null;
  return (
    <div className="vai-flex vai-justify-between">
      <dt className="vai-text-gray-600">{label}:</dt>
      <dd className="vai-font-medium">{value}</dd>
    </div>
  );
};
